import { useEffect, useMemo, useRef, useState } from 'react'
import { useApiClients } from '@/api/api-clients-context'
import { LinkGrantRateLimitedError, LinkNoLongerUsableError } from '@/api/auth-link-grants-client'
import { HttpError } from '@/api/http-error'
import { ScanUnavailableError } from '@/api/scan-client'
import { submitWithRetry } from '@/api/submit-with-retry'
import { toSubmitScanRequest } from '@/api/to-submit-request'
import type { ScanConfigResponse, ScanFailureReason, ScanVendor, SubmitScanRequest } from '@/api/types'
import { scanCalibration, toMindsetGender, toSdkSex } from '@/lib/demographics'
import { I18nProvider } from '@/i18n/i18n-context'
import { resolveLanguage, type SupportedLanguage, type TranslationKey } from '@/i18n/translations'
import { useFhVitalsClient } from '@/sdk/fh-vitals-client-context'
import { describeScanEvent, type ScanEventDescription } from '@/sdk/fh-vitals-events'
import { runScanSession } from '@/sdk/fh-vitals-session'
import { runHybridScanSession } from '@/sdk/hybrid-session'
import { useMindsetClientFactory } from '@/sdk/mindset-client-context'
import { useShenAiClientFactory } from '@/sdk/shenai-client-context'
import { runShenAiScanSession } from '@/sdk/shenai-session'
import { attachCameraToVideo, cloneCameraForSecondReader, openPatientCamera } from '@/sdk/patient-camera'
import type { MindsetPhase } from '@/sdk/mindset-session'
import { runMindsetScanSession } from '@/sdk/mindset-session'
import type { RuntimeConfig } from '@/lib/runtime-config'
import { readLanguageChoice, storeLanguageChoice } from '@/lib/language-choice'
import { forgetScanSession, readScanSession, storeScanSession } from '@/lib/scan-session'
import { forgetStallReloads, thisPage, useStallWatchdog, type PreparationStage } from '@/lib/stall-recovery'
import { BrandLogo } from '@/ui/brand-logo'
import { CompleteScreen } from './screens/complete-screen'
import { ThankedScreen } from './screens/thanked-screen'
import { CouldNotLoadScreen } from './screens/could-not-load-screen'
import { DateOfBirthScreen } from './screens/date-of-birth-screen'
import { DeadLinkScreen } from './screens/dead-link-screen'
import { StartScreen } from './screens/start-screen'
import { LoadingScreen } from './screens/loading-screen'
import { RateLimitedScreen } from './screens/rate-limited-screen'
import { ScanFailedScreen } from './screens/scan-failed-screen'
import { ScanUnavailableScreen } from './screens/scan-unavailable-screen'
import { SCAN_CANVAS_ID, SCAN_VIDEO_ID, ScanningScreen } from './screens/scanning-screen'
import { CameraPermissionScreen } from './screens/camera-permission-screen'
import type { FlowState } from './flow-state'

/**
 * The body measurements FaceHeart's startMeasuring takes, fixed rather than asked for
 * (docs/decisions.md D-15). Mindset is given neither - its session config carries `{age, gender}`
 * and nothing else - so on that path these are not read at all.
 *
 * They were a form the patient filled in. Nothing this app does with them justified the two screens
 * of friction: what arrived was a patient's own estimate of their own body, typed on a phone, into
 * a vendor parameter whose effect on the reading nobody here can characterise.
 */
const SCAN_DEMOGRAPHICS = { heightCm: 175, weightKg: 96 } as const

const CAMERA_PREPARE_SECONDS = 5
const MEASURE_WINDOW_MS = 55_000
const ASSETS_FOLDER_PATH = '/sdk/fhvitals_v2/'

/**
 * Mindset's three windows. The measurement one is longer than FaceHeart's 55 seconds because the
 * vendor allows a session up to three attempts internally and ends it itself as soon as its models
 * converge - this is the cap for a session that never does, and the reference integration in
 * `SH/multi-sdk` settled on the same 110 seconds after watching sessions that did not stop.
 *
 * The ready window covers the 55 MB of WASM, model data and MediaPipe assets the SDK loads before it
 * says anything at all, which on a cold cellular connection is the slowest thing in this flow.
 */
const MINDSET_READY_TIMEOUT_MS = 60_000
const MINDSET_AUTHORIZED_VITALS_TIMEOUT_MS = 5_000
const MINDSET_MEASURE_WINDOW_MS = 110_000

/**
 * Shen.AI's windows.
 *
 * THE LOAD WINDOW IS THREE MINUTES, and it was sixty seconds shared with the licence call. Sixty is
 * a fair number for one request to one host and a hostile one for 33 MB on a phone: a cellular
 * connection can spend longer than that on the download alone, and what the patient got for it was
 * "the scanner could not finish loading" on a scan that was working perfectly (docs/decisions.md
 * D-34). The vendor now reports its own download progress, so this window bounds a download that has
 * genuinely stopped rather than one that is merely slow - and the patient can see the difference.
 *
 * The licence window is short for the opposite reason: it is one request, and nothing about a slow
 * device makes it slower.
 *
 * The measurement window is the ONE_MINUTE preset's minute plus room for a slow finalize; the
 * vendor ends its own session when the preset's clock runs out, and this bounds a session that does
 * not, exactly as the Mindset window does.
 *
 * The poll interval is this vendor's alone: Shen emits nothing and is read on a timer. Half a second
 * is fast enough that a patient drifting out of frame is told within a blink, and slow enough that
 * the loop is not competing with the measurement for the main thread.
 */
const SHENAI_LOAD_TIMEOUT_MS = 180_000
const SHENAI_LICENCE_TIMEOUT_MS = 20_000
const SHENAI_MEASURE_WINDOW_MS = 90_000
const SHENAI_POLL_INTERVAL_MS = 500

/**
 * The hybrid vendor's one number of its own: how long BOTH runtimes get to arrive
 * (docs/decisions.md D-37).
 *
 * It is Shen.AI's three minutes rather than Mindset's sixty seconds, applied to both halves, and the
 * reason is arithmetic rather than generosity. The two downloads - 55 MB of Mindset bundle, WASM and
 * MediaPipe assets, and 33 MB of Shen.AI runtime - come down ONE connection at the same time, so each
 * of them takes longer than it does alone; a budget measured against one vendor loading by itself is
 * the wrong budget for two, and what a patient gets for it is "the scanner could not finish loading"
 * on a scan that was downloading perfectly normally. That is D-34's argument exactly, with a second
 * vendor added to the same pipe.
 *
 * Every other window on this path is each vendor's own, unchanged: two SDKs sharing a connection do
 * not make either one's licence call or measurement any slower.
 */
const HYBRID_READY_TIMEOUT_MS = 180_000

/**
 * The hybrid scan's cap on Shen.AI's measurement: its thirty-second preset plus room to finalize
 * (docs/decisions.md D-40).
 *
 * **Mindset has no equivalent number here, and that is a fact about the vendor rather than an
 * omission.** Its session length is not a client setting: the SDK computes
 * `maxSessionTime = max(vital.max_session_time)` over the vitals the LICENCE authorizes - the vendor's
 * own `vital-client.js`, readable in the sourcemap this repository vendors beside the bundle - and
 * ends the attempt either at that time or as soon as every SELECTED vital has signalled ready. So a
 * thirty-second Mindset measurement cannot be asked for. It can only be taken, by this app calling
 * `stop()` early, which is `MINDSET_MEASURE_WINDOW_MS` - and that window stays at the full 110
 * seconds deliberately, because cutting it is not a shorter measurement, it is a measurement
 * interrupted, and the pulse the composite requires is what would go missing.
 *
 * The consequence is worth being clear about: the two engines now overlap for about thirty seconds
 * rather than for the whole scan, and Mindset then runs on alone. The patient's scan is no shorter.
 * What is shorter is the part where a phone is holding two measuring runtimes at once, which is the
 * part that was killing it (D-38).
 */
const HYBRID_SHENAI_MEASURE_WINDOW_MS = 45_000

/**
 * How long the patient is asked to hold still on a hybrid scan, across BOTH halves
 * (docs/decisions.md D-42).
 *
 * TWO MINUTES, from the moment the first engine starts measuring - not from the tap, because what
 * comes before that is a download the patient cannot hurry and is not being asked to sit still for.
 *
 * **It was 1:10, which was the right number for a thirty-second first half, and there is no
 * thirty-second first half.** The vendor's shorter presets do not measure on this build
 * (docs/decisions.md D-40), so the first half is the one-minute preset's minute - and 1:10 would
 * leave the second vendor ten seconds to download 55 MB, load a WASM module and measure a pulse,
 * which is not a budget, it is a formality. A minute each is what makes the second half a real
 * measurement rather than a line in a log saying it ran out of time.
 *
 * The reading is submitted at the end of the FIRST minute either way, so what this number buys is the
 * second vendor's session, and what it costs is the patient's time. It is one constant, and cutting it
 * costs nothing but the second half.
 */
const HYBRID_HOLD_WINDOW_MS = 120_000

/**
 * What either vendor's session hands back, in this app's own vocabulary rather than the SDK's. The
 * two runners disagree about almost everything - one returns a frame of vitals, the other a nested
 * per-model result object - and this is the seam at which that stops being the flow's problem.
 */
/** The service's answer to a scoped token it no longer accepts (ScanTokenFilter): verify again. */
function isUnauthorized(error: unknown): boolean {
  return error instanceof HttpError && error.status === 401
}

type VendorScanOutcome =
  | { readonly outcome: 'measured'; readonly request: SubmitScanRequest }
  | { readonly outcome: 'no-usable-result' }
  | { readonly outcome: 'error'; readonly messageKey: TranslationKey }

export function ScanFlow({ token, runtimeConfig }: { token: string; runtimeConfig: RuntimeConfig }) {
  const { auth, scan, mindset, shenai } = useApiClients()
  const fhVitalsClient = useFhVitalsClient()
  const mindsetClientFactory = useMindsetClientFactory()
  const shenAiClientFactory = useShenAiClientFactory()
  const [state, setState] = useState<FlowState>({ step: 'challenging' })

  /**
   * The camera, opened in the patient's tap and handed to whichever vendor client runs (D-24).
   *
   * A ref and not state: nothing renders differently because of it, and a MediaStream in the state
   * union would make every transition a question about who stops the tracks. The vendor client owns
   * it from the moment it is passed in - `destroy()` stops every track - which is why nothing here
   * clears this beyond dropping the handle.
   */
  const cameraRef = useRef<MediaStream | null>(null)

  /**
   * The shape of the frames the camera is delivering, for the one surface that has to crop them.
   *
   * Reported BY THE SHEN.AI CLIENT, off the stream it already holds - this flow does not open a
   * camera for that vendor and must not start: a stream opened in the tap and then left unconsumed
   * through a 33 MB load is a live camera nobody has asked a frame of, which is D-25's black preview
   * and, on this path, a vendor that never sees a face.
   */
  const [previewAspect, setPreviewAspect] = useState<number | null>(null)

  /**
   * How many times this patient has been shown a scan that produced nothing.
   *
   * A REF rather than state, because nothing renders from it and the effect that reads it is rebuilt
   * on every state change - a captured count would be one attempt behind exactly when it decides
   * which screen the patient gets.
   */
  const failedAttempts = useRef(0)

  /**
   * ONE RETRY, AND THEN THE PATIENT IS THANKED AND LET GO.
   *
   * The first failure offers *Try again*, because a scan is often retrievable by having another go -
   * better light, a steadier hand, a face further from the lens. The second is not: the patient has
   * now held a phone to their face twice, and a third invitation is this page asking them to keep
   * paying for a fault they cannot see and did not cause. So the second one says thank you and says
   * nothing about the failure (`thanked`), and the missing reading becomes work for the care team
   * instead of news for the patient.
   */
  const failScan = (
    messageKey: TranslationKey,
    accessToken: string,
    config: ScanConfigResponse,
  ) => {
    /**
      * Reported before anything is rendered, and NOT awaited.
      *
      * Not awaited because the patient is owed their screen now and this is a side channel: a report
      * that is still in flight must not hold up the end of somebody's journey, and one that fails
      * outright must not turn a scan that merely produced nothing into a page that says something
      * else went wrong. The service counts what reaches it; a report lost to a closing tab is a
      * count that stays where it was, which is the same as the attempt never being made.
      */
    void scan
      .reportScanFailure(accessToken, reasonFor(messageKey))
      .catch((error: unknown) => console.warn('[scan] the failed attempt was not reported', error))

    if (failedAttempts.current >= 1) {
      console.warn('[scan] a second attempt produced nothing; the patient is thanked, not asked again')
      setState({ step: 'thanked' })
      return
    }

    failedAttempts.current += 1
    setState({ step: 'scan-failed', messageKey, accessToken, config })
  }
  /**
   * The patient's own choice, if this tab has one - read once, before anything renders, because it
   * outranks the enrolled language and a first paint in the wrong one is a flicker on the screen the
   * choice was made on.
   */
  const chosenLanguage = useMemo(() => readLanguageChoice(token), [token])
  const [language, setLanguage] = useState(chosenLanguage ?? resolveLanguage(null))

  /**
   * Whether the patient has picked a language for themselves.
   *
   * It exists so the enrolled one cannot come back and take it away. The scan config sets the
   * language when it lands, and the flow FETCHES IT AGAIN whenever a scoped token is refused
   * (`verifyAgain` sends `loading-config` round a second time) - so without this, a 401 in the middle
   * of a journey would silently put the page back into the language the record says rather than the
   * one the person reading it asked for.
   */
  const languageWasChosen = useRef(chosenLanguage !== null)

  const chooseLanguage = (next: SupportedLanguage) => {
    languageWasChosen.current = true
    storeLanguageChoice(token, next)
    setLanguage(next)
  }
  /**
   * Every signal the running vendor is raising, which the scanning screen shows in red.
   *
   * A LIST since Mindset sends several at once (docs/decisions.md D-28). FaceHeart reports one event at
   * a time, so its path passes a list of one - the screen does not need to know which SDK it is
   * rendering for.
   */
  const [troubles, setTroubles] = useState<readonly ScanEventDescription[]>([])
  /**
   * How far through the measurement the vendor says it is, as a fraction of one, or null while no
   * vendor has said anything - which on the FaceHeart path is the whole scan, because that SDK reports
   * state names and not progress. The scanning screen renders the null as an indeterminate bar rather
   * than as a bar at zero or as a clock of its own (scanning-screen.tsx explains at length why it is
   * not allowed to invent one).
   *
   * Held here beside `trouble` rather than inside the screen because the screen is presentational and
   * the vendor session runs in this component's effect; it is not in FlowState for the same reason
   * `trouble` is not - a value that changes once a second is not a step of the flow, and putting it
   * there would restart the effect that owns the scan on every tick.
   */
  const [progress, setProgress] = useState<number | null>(null)

  /**
   * Which phase the vendor session is in, for the sentence the scanning screen shows.
   *
   * The bar cannot say this: it only moves once the vendor starts ticking, a minute into a scan on a
   * phone, so everything before that looked the same as stuck (docs/decisions.md D-26).
   */
  const [phase, setPhase] = useState<MindsetPhase | null>(null)

  /**
   * How many times the running vendor has said anything at all during its prologue.
   *
   * COUNTED RATHER THAN READ, because what the stall watchdog over the prologue needs is not how far
   * along the vendor is but whether it is still there (docs/decisions.md D-46): the same download
   * fraction reported twice is a vendor working through a bad connection, and a bar that has not
   * moved for a minute on a vendor that keeps reporting is slow rather than stuck.
   */
  const [heardFromVendor, setHeardFromVendor] = useState(0)

  /**
   * Whatever this page is waiting on, watched for a stall (docs/decisions.md D-46).
   *
   * The two stages in front of the start screen are one HTTP request each with nothing bounding them;
   * the third is the vendor prologue, which is bounded only by three minutes a patient cannot tell
   * from forever. Everything else answers null - a measurement the patient is holding still for above
   * all, and every screen that is waiting for a person rather than for a response.
   */
  const exhausted = useStallWatchdog(token, watchedStage(state, phase), heardFromVendor)

  /**
   * The budget, handed back at the first moment an attempt has demonstrably worked - which is a
   * vendor measuring, and not the start screen it used to be.
   *
   * `ready` was the honest answer while the watchdog stopped there: the budget was spent getting to
   * that screen. A prologue stall now reloads ONTO that screen, so clearing it there would hand back
   * two fresh reloads on every pass and leave a budget that is never actually spent - two per attempt
   * forever, which is the loop it exists to prevent with a tap between each turn of it. `measuring` is
   * the first point at which everything in front of it - the config, the handshake, the scan config,
   * the download, the licence - has been proved to work in this tab.
   *
   * The FaceHeart path announces no phase and so never clears it. That budget expires instead
   * (`RELOAD_BUDGET_WINDOW_MS`), which is what the expiry is for and is the whole cost of not asking
   * a silent SDK a question it cannot answer.
   */
  useEffect(() => {
    if (phase === 'measuring') {
      forgetStallReloads(token)
    }
  }, [phase, token])

  // Step 1 (architecture §5): challenge, then satisfy whatever factor it names - which for a scan
  // link is none at all.
  //
  // A vitals scan grant carries LinkGrantFactor.None (docs/decisions.md D-14): the patient is being
  // asked to hold a phone to their face, not to agree to anything, so there is nothing for them to
  // confirm and no screen to show. `verify` still runs, because that is what mints the scoped
  // access token every later call carries - the handshake satisfies a None policy on the policy
  // itself and compares no factor, so the value sent is immaterial and an empty one is the honest
  // thing to send.
  //
  // It runs ONCE per link per tab. Every verify counts against Auth's hourly cap, and a reload is
  // not a new patient, so a token this tab already holds is picked up and Auth is not asked again
  // (docs/decisions.md D-45). A held token the service refuses is forgotten below, and this step
  // runs afresh.
  //
  // The factor is read from the CHALLENGE rather than assumed, so a grant minted with a factor -
  // a consent link, or a scan link from before this changed - still meets its screen.
  useEffect(() => {
    if (state.step !== 'challenging') return

    const held = readScanSession(token)
    if (held) {
      setState({ step: 'loading-config', accessToken: held })
      return
    }

    let cancelled = false
    auth
      .challenge(token)
      .then(async (result) => {
        if (cancelled) return

        if (result.factor !== 'None') {
          setState({ step: 'date-of-birth', factor: result.factor })
          return
        }

        const verified = await auth.verify(token, '')
        if (!cancelled) {
          storeScanSession(token, verified)
          setState({ step: 'loading-config', accessToken: verified.accessToken })
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof LinkGrantRateLimitedError) {
          setState({ step: 'rate-limited' })
        } else if (error instanceof LinkNoLongerUsableError) {
          setState({ step: 'dead-link' })
        } else {
          throw error
        }
      })
    return () => {
      cancelled = true
    }
  }, [state, auth, token])

  function verifyAgain() {
    forgetScanSession(token)
    setState({ step: 'challenging' })
  }

  // Step 3-4 (architecture §5): fetch scan config, which is also where the page's language is set —
  // from the token, never from the browser (architecture §9).
  useEffect(() => {
    if (state.step !== 'loading-config') return
    let cancelled = false
    scan
      .getScanConfig(state.accessToken)
      .then((config) => {
        if (cancelled) return
        // The enrolled culture is the DEFAULT and not the decision - see `languageWasChosen`.
        if (!languageWasChosen.current) {
          setLanguage(resolveLanguage(config.language))
        }
        setState({ step: 'ready', accessToken: state.accessToken, config })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof ScanUnavailableError) {
          setState({ step: 'scan-unavailable' })
        } else if (isUnauthorized(error)) {
          verifyAgain()
        } else {
          throw error
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- verifyAgain closes over the token this flow owns.
  }, [state, scan])

  /**
   * The preview, live the moment the scan screen exists.
   *
   * Separate from the effect below on purpose, and it runs first: that one loads the vendor - 55 MB of
   * bundle and an 11 MB WASM module, tens of seconds on a phone - before the vendor asks for a preview.
   * A camera track held open for that long with nothing consuming a frame is what iOS showed as a black
   * preview, permission granted and stream live (docs/decisions.md D-25). It is also what the patient
   * should see: their own face while the vendor loads, rather than a black rectangle they cannot tell
   * from a fault.
   */
  useEffect(() => {
    if (state.step !== 'scanning') return
    const stream = cameraRef.current
    if (!stream) return

    const video = document.getElementById(SCAN_VIDEO_ID)
    if (!(video instanceof HTMLVideoElement)) return

    void attachCameraToVideo(video, stream)
  }, [state.step])

  // Step 5-6 (architecture §5): mark started, run the vendor scan, submit whatever it measured.
  useEffect(() => {
    if (state.step !== 'scanning') return
    let cancelled = false
    const { accessToken, config } = state
    setTroubles([])
    // Cleared with `trouble`, and for the same reason: a retry after a failed scan renders this screen
    // again, and a bar left sitting at 90% from the attempt before it is a claim about this attempt.
    setProgress(null)
    // Same reasoning as `progress`: a phase left over from the attempt before this one is a claim about
    // this one.
    setPhase(null)

    /**
     * What the vendor says, on its way to the screen and to the stall watchdog.
     *
     * The count is kept only until the measurement starts, because after that the screen is driven by
     * a poll that reports the same fraction twice a second and a re-render per tick would be paid for
     * a watchdog that has stopped watching.
     */
    let inPrologue = true
    const heard = () => {
      if (inPrologue) setHeardFromVendor((times) => times + 1)
    }

    const showProgress = (fractionComplete: number) => {
      if (cancelled) return
      setProgress(fractionComplete)
      heard()
    }

    const showPhase = (next: MindsetPhase) => {
      if (cancelled) return
      setPhase(next)
      inPrologue = next !== 'measuring'
      heard()
    }

    /**
     * The patient's own age and gender, off the scan config the service answered - which is where
     * they have been all along, resolved next to the patient record and carried on the create
     * request, rather than anything this page asks for or invents.
     *
     * Read ONCE, here, and handed to whichever vendor runs: both calibrate blood pressure with it,
     * and a scan whose assessment carried neither must not calibrate the two vendors differently.
     * `scanCalibration` is what puts a floor under the fields (`Age` is an optional int on the
     * create request, so "not supplied" arrives as `age: 0` and `Gender.Unknown`) - see
     * src/lib/demographics.ts.
     */
    const calibration = scanCalibration(config)
    console.log('[scan] calibrating from the assessment', {
      sent: { age: config.age, gender: config.gender },
      using: calibration,
    })

    /**
     * FaceHeart: the SDK licenses itself, opens the camera itself and draws onto the canvas the
     * screen rendered. Its result is a frame of vitals, mapped here to the submit shape.
     */
    async function runFaceHeartScan(): Promise<VendorScanOutcome> {
      const outcome = await runScanSession(
        fhVitalsClient,
        {
          heightCm: SCAN_DEMOGRAPHICS.heightCm,
          weightKg: SCAN_DEMOGRAPHICS.weightKg,
          age: calibration.age,
          sex: toSdkSex(calibration.gender),
        },
        {
          cameraPrepareSeconds: CAMERA_PREPARE_SECONDS,
          measureWindowMs: MEASURE_WINDOW_MS,
          assetsFolderPath: ASSETS_FOLDER_PATH,
          authUrl: runtimeConfig.faceHeartAuthUrl,
          virtualId: runtimeConfig.faceHeartVirtualId,
          canvasId: SCAN_CANVAS_ID,
        },
        (event) => {
          if (cancelled) return
          // FaceHeart reports one event at a time, and a null description means "nothing to say".
          const description = describeScanEvent(event)
          setTroubles(description ? [description] : [])
        },
      )

      if (outcome.outcome !== 'measured') {
        return outcome
      }

      const request = toSubmitScanRequest(outcome.result)
      return request ? { outcome: 'measured', request } : { outcome: 'no-usable-result' }
    }

    /**
     * Mindset: this app opens the camera, and each run is licensed by a call through this service -
     * which is what the `authenticator` closure below is. The clinic's API key stays on the server
     * and the browser only ever sees the licence it minted (docs/decisions.md D-11).
     */
    async function runMindsetScan(): Promise<VendorScanOutcome> {
      const video = document.getElementById(SCAN_VIDEO_ID)
      if (!(video instanceof HTMLVideoElement)) {
        return { outcome: 'error', messageKey: 'scanCouldNotStart' }
      }

      // One call, one job: the SDK's run token in, the vendor's licence out. A failure here is a
      // scan that could not start like any other - the route decides nothing about whether this
      // assessment is still live, because GET /v1/public/scan and POST /v1/public/scan/start
      // already did.
      const client = mindsetClientFactory({
        authenticator: (runToken) => mindset.authorizeRun(accessToken, runToken),
        // Opened in the tap, seconds ago, by onStart - see the note there.
        stream: cameraRef.current,
      })

      return runMindsetScanSession(
        client,
        { age: calibration.age, gender: toMindsetGender(calibration.gender) },
        {
          readyTimeoutMs: MINDSET_READY_TIMEOUT_MS,
          authorizedVitalsTimeoutMs: MINDSET_AUTHORIZED_VITALS_TIMEOUT_MS,
          measureWindowMs: MINDSET_MEASURE_WINDOW_MS,
          video,
        },
        (descriptions) => !cancelled && setTroubles(descriptions),
        showProgress,
        showPhase,
        /**
         * The vendor's own copy of the measurement, when this installation asks for one
         * (docs/decisions.md D-33).
         *
         * NOT AWAITED, and not guarded by `cancelled`. Not awaited because nothing downstream needs
         * it: the platform's submit is what completes the assessment, and making the patient's
         * "scan complete" screen wait on a third party's API would put a vendor between them and
         * the end of their scan. Not guarded by `cancelled` because that flag means this component
         * is going away, which is exactly when a fire-and-forget copy still ought to be sent - the
         * measurement has already been taken either way.
         *
         * `forwardResult` resolves rather than rejects on failure, so there is deliberately no
         * `.catch` here to forget.
         */
        (result) => {
          if (config.forwardResultsToVendor) {
            void mindset.forwardResult(accessToken, result)
          }
        },
      )
    }

    /**
     * Shen.AI: this app opens the camera and mints the vendor's token through this service, which
     * holds an admin key the browser never sees (docs/decisions.md D-32).
     *
     * The vendor renders into a CANVAS rather than reading a `<video>`, so the scanning screen's
     * canvas is what it is handed - and with its own interface switched off it draws the preview and
     * nothing else, under this app's own guide, phase text and signal banner.
     */
    async function runShenAiScan(): Promise<VendorScanOutcome> {
      let minted: { token: string; userId: string }
      try {
        // Minted BEFORE the 33 MB runtime loads, deliberately: a token that cannot be obtained is a
        // scan that cannot start, and finding that out after a minute of downloading is a minute of
        // a patient's time spent on a foregone conclusion.
        minted = await shenai.mintToken(accessToken)
      } catch (error) {
        console.warn('[shenai] the token could not be minted', error)
        return { outcome: 'error', messageKey: 'scanCouldNotStart' }
      }

      const client = shenAiClientFactory({
        // Opened in the tap, seconds ago, by onStart - see the note there.
        stream: cameraRef.current,
        canvasId: SCAN_CANVAS_ID,
        onCameraAspect: (ratio) => !cancelled && setPreviewAspect(ratio),
      })

      return runShenAiScanSession(
        client,
        minted.token,
        minted.userId,
        {
          loadTimeoutMs: SHENAI_LOAD_TIMEOUT_MS,
          licenceTimeoutMs: SHENAI_LICENCE_TIMEOUT_MS,
          measureWindowMs: SHENAI_MEASURE_WINDOW_MS,
          pollIntervalMs: SHENAI_POLL_INTERVAL_MS,
        },
        (descriptions) => !cancelled && setTroubles(descriptions),
        showProgress,
        showPhase,
      )
    }

    /**
     * Both SDKs, over one camera, submitting a reading composed from the two (docs/decisions.md
     * D-37).
     *
     * Everything this function does is arrange what the two single-vendor branches above already
     * arrange - a licensed Mindset run, a minted Shen.AI token, one camera opened in the tap - and
     * hand both to `runHybridScanSession`, which owns the sequencing: both ready before either
     * measures, both measuring at once, the scan ending with the last of them.
     */
    /**
     * Whether the platform's reading has ALREADY been submitted, mid-scan, by the sequential runner.
     *
     * A plain closure variable rather than state: nothing renders differently because of it, it lives
     * exactly as long as this one attempt, and putting it in `FlowState` would restart the effect
     * that owns the scan (the same argument `trouble` and `progress` make about themselves).
     */
    let readingAlreadySubmitted = false

    async function runHybridScan(): Promise<VendorScanOutcome> {
      const video = document.getElementById(SCAN_VIDEO_ID)
      if (!(video instanceof HTMLVideoElement)) {
        return { outcome: 'error', messageKey: 'scanCouldNotStart' }
      }

      let minted: { token: string; userId: string }
      try {
        // Minted BEFORE either runtime loads, for the reason the Shen.AI branch gives: a token that
        // cannot be obtained is a scan that cannot start, and on this path finding that out late
        // costs the patient two downloads rather than one.
        minted = await shenai.mintToken(accessToken)
      } catch (error) {
        console.warn('[hybrid] the shenai token could not be minted', error)
        return { outcome: 'error', messageKey: 'scanCouldNotStart' }
      }

      const mindsetClient = mindsetClientFactory({
        authenticator: (runToken) => mindset.authorizeRun(accessToken, runToken),
        // The camera opened in the patient's tap, seconds ago, by onStart.
        stream: cameraRef.current,
      })

      const shenAiClient = shenAiClientFactory({
        // A CLONE of that same camera, not a second getUserMedia: one device, one permission, two
        // readers, and a teardown that does not blind the half still measuring
        // (cloneCameraForSecondReader says why at length).
        stream: cloneCameraForSecondReader(cameraRef.current),
        canvasId: SCAN_CANVAS_ID,
        // Nothing drawn on that canvas is ever seen on this path - the Mindset video is stacked on
        // top of it, opaque - so the vendor's mesh is GPU work for no viewer (docs/decisions.md D-38).
        drawFaceMask: false,
        // The vendor's own named thirty-second preset, where the single-vendor scan runs the
        // one-minute one (docs/decisions.md D-40).
        measurement: 'thirtySecondsAllMetrics',
      })

      const outcome = await runHybridScanSession(
        mindsetClient,
        shenAiClient,
        { age: calibration.age, gender: toMindsetGender(calibration.gender) },
        minted,
        {
          mindset: {
            // Both runtimes share one connection, so the load budget is the generous one - see
            // HYBRID_READY_TIMEOUT_MS.
            readyTimeoutMs: HYBRID_READY_TIMEOUT_MS,
            authorizedVitalsTimeoutMs: MINDSET_AUTHORIZED_VITALS_TIMEOUT_MS,
            measureWindowMs: MINDSET_MEASURE_WINDOW_MS,
            video,
          },
          shenAi: {
            loadTimeoutMs: HYBRID_READY_TIMEOUT_MS,
            licenceTimeoutMs: SHENAI_LICENCE_TIMEOUT_MS,
            // The thirty-second preset's window plus room for a slow finalize, which is the same
            // shape as the single-vendor number (a minute plus thirty) and not a coincidence: this
            // bounds a session that does not stop itself, and the vendor's own clock is what
            // normally stops it (docs/decisions.md D-40).
            measureWindowMs: HYBRID_SHENAI_MEASURE_WINDOW_MS,
            pollIntervalMs: SHENAI_POLL_INTERVAL_MS,
          },
          holdWindowMs: HYBRID_HOLD_WINDOW_MS,
        },
        (descriptions) => !cancelled && setTroubles(descriptions),
        showProgress,
        showPhase,
        /**
         * The platform's reading, submitted THE MOMENT THE FIRST VENDOR PRODUCES IT - about thirty
         * seconds in, and a minute before this scan ends (docs/decisions.md D-42).
         *
         * **Not awaited, and deliberately not guarded by `cancelled`.** Not awaited because the
         * second half starts immediately and a network round trip must not delay it. Not guarded
         * because `cancelled` means this component is going away, and a measurement that has been
         * taken must still reach the platform when it does - this is the patient's actual reading,
         * which is a stronger version of the argument D-33 makes about the vendor's copy.
         *
         * On success `readingAlreadySubmitted` spares `run()` a second, identical submit. The service
         * is first-wins idempotent so a duplicate would be harmless, but a retry storm against a
         * completed assessment is noise nobody needs to read.
         */
        (request) => {
          void submitWithRetry(scan, accessToken, request)
            .then(() => {
              readingAlreadySubmitted = true
              console.log('[hybrid] the platform reading is submitted; the rest is a side channel')
            })
            .catch((error: unknown) => {
              // Reported and dropped. `run()` still holds the same request and submits it again at
              // the end of the scan, which is where a failure reaches the patient as a sentence.
              console.warn('[hybrid] the early submit failed; the scan will retry it at the end', error)
            })
        },
        /**
         * Mindset's own measurement, forwarded to Mindset's own records exactly as the single-vendor
         * path forwards it (docs/decisions.md D-33): the vendor's structure, unmodified, and never
         * what was submitted - that reading is the other vendor's, and writing it into this vendor's
         * records would attribute a measurement to a vendor that never made it.
         *
         * Not awaited and not guarded by `cancelled`, for the reasons the Mindset branch above
         * records.
         */
        (result) => {
          if (config.forwardResultsToVendor) {
            void mindset.forwardResult(accessToken, result)
          }
        },
      )

      return outcome
    }

    async function run() {
      try {
        await scan.startScan(accessToken)
      } catch (error) {
        if (cancelled) return
        if (error instanceof ScanUnavailableError) {
          setState({ step: 'scan-unavailable' })
          return
        }
        if (isUnauthorized(error)) {
          verifyAgain()
          return
        }
        throw error
      }

      // The vendor is the server's answer, read off the scan config (docs/decisions.md D-11). Every
      // SDK is in this bundle and none is loaded until this line picks one - and one of the answers
      // picks two of them (docs/decisions.md D-37).
      const outcome =
        config.vendor === 'Mindset'
          ? await runMindsetScan()
          : config.vendor === 'ShenAi'
            ? await runShenAiScan()
            : config.vendor === 'HybridMindsetShenAi'
              ? await runHybridScan()
              : await runFaceHeartScan()

      if (cancelled) return

      if (outcome.outcome === 'error') {
        failScan(outcome.messageKey, accessToken, config)
        return
      }
      if (outcome.outcome !== 'measured') {
        failScan('noClearReading', accessToken, config)
        return
      }
      const request = outcome.request

      if (readingAlreadySubmitted) {
        // A sequential hybrid scan submits its reading thirty seconds in, before the second vendor
        // is even loaded (docs/decisions.md D-42). Submitting it again here would be idempotent and
        // pointless.
        setState({ step: 'complete', results: request })
        return
      }

      try {
        // The measurement is in hand and the scan is not repeatable without asking the patient for
        // another fifty-five seconds, so a transient failure here is retried rather than discarded.
        // `request` stays in this closure across every attempt, which is what makes the retry a
        // retry of the SUBMIT and not of the scan.
        await submitWithRetry(scan, accessToken, request, undefined, () => cancelled)
        setState({ step: 'complete', results: request })
      } catch (error) {
        if (cancelled) return
        if (error instanceof ScanUnavailableError) {
          setState({ step: 'scan-unavailable' })
          return
        }
        if (isUnauthorized(error)) {
          verifyAgain()
          return
        }
        failScan('couldNotSendResults', accessToken, config)
      }
    }

    run().catch(() => {
      if (!cancelled) {
        // A translated sentence, never the exception's own words. An HttpError's message is
        // "submit failed with 500" and a vendor error is a close code; either one shown to a
        // patient on a medical page tells them nothing they can act on and quite a lot about the
        // inside of the service. What went wrong is for the logs, and the browser console already
        // has it.
        failScan('scanFailedDetail', accessToken, config)
      }
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- verifyAgain closes over the token this flow owns.
  }, [state, scan, mindset, shenai, fhVitalsClient, mindsetClientFactory, shenAiClientFactory, runtimeConfig])

  /**
   * The patient's tap, and the one place the camera may be opened.
   *
   * `openPatientCamera()` is called SYNCHRONOUSLY from this handler and nothing is awaited before it.
   * On iOS - every browser there is WebKit, including Chrome for iPhone - `getUserMedia` is refused
   * once the user activation from the tap has expired, and it is refused SILENTLY: no prompt, no
   * question, a rejected promise. The scan used to leave the camera to the vendor client, which asked
   * for it after `init()` had spent seconds loading 55 MB of bundle and WASM, so on a phone the
   * patient was never asked at all and the scan simply stopped (docs/decisions.md D-24).
   *
   * The stream lives in a ref rather than in `FlowState`: it is not a fact about which screen the
   * patient is on, it is a device handle, and putting a MediaStream in a state union would make every
   * transition a decision about who closes it.
   */
  async function onStart() {
    if (state.step !== 'ready') return
    const { accessToken, config } = state

    // ONLY for the vendors this app opens the camera for. FaceHeart's SDK opens its own - it patches
    // `document.createElement` to get a video element it controls - so a stream acquired here would be
    // a second handle on the same device that nothing hands over and nothing stops. That SDK has the
    // same iOS exposure inside its own code, seconds after its own load, and it cannot be fixed from
    // out here; every scan this service issues is Mindset today (GetScanConfigCommand).
    //
    // The hybrid vendor is here for a sharper version of the same reason: it needs the camera opened
    // ONCE and split, because two `getUserMedia` calls are two device handles and the second of them
    // is refused on iOS (docs/decisions.md D-24, D-37).
    if (config.vendor === 'Mindset' || config.vendor === 'HybridMindsetShenAi') {
      // Synchronous, so it is still nothing awaited before the camera call: what the patient sees
      // behind the permission dialog is the one line about it (docs/decisions.md D-49).
      setState({ step: 'opening-camera', accessToken, config })
      try {
        // Half the frame rate when two engines are going to read this one stream, which is a
        // measurement neither vendor loses anything by and a per-frame cost a phone running both
        // cannot pay twice (docs/decisions.md D-38).
        cameraRef.current = await openPatientCamera({
          sharedByTwoSdks: config.vendor === 'HybridMindsetShenAi',
        })
      } catch {
        // Refused, or held by another app. Its own sentence, because "reopen your link" cannot fix it.
        failScan('cameraUnavailable', accessToken, config)
        return
      }
    }

    setState({ step: 'scanning', accessToken, config })
  }

  return (
    <I18nProvider language={language}>
      {/*
        * The mark on every screen, once, HERE rather than in each screen or inside `Card`.
        *
        * It was on the start screen alone. Every other screen a patient sees - the date-of-birth
        * question, the scan itself, the thank-you, and every failure - carried nothing saying whose page
        * this is, which for a link that arrives by text message is the one thing worth saying on all of
        * them. In the layout and not in `Card`, because `Card` is a box and branding is not a property of
        * boxes; a screen that one day wants two cards should not get two logos.
        *
        * ANCHORED TO THE TOP, AND CENTRED, and it was neither. The header sat inside a
        * `justify-center` column with the screen, so its position was a function of how tall the
        * current screen happened to be: the mark sat lower on the short date-of-birth question than on
        * the tall scanning screen and moved every time the flow advanced. A logo that jumps as the
        * content changes reads as the page reloading, on a flow whose whole job is to look calm while
        * a patient holds a phone to their face.
        *
        * So the header is its own row at the top of the viewport and does not move for anything, and
        * the screen starts directly beneath it rather than being centred in what is left. `shrink-0`
        * is what stops a tall screen squeezing it, which on the scanning screen it otherwise does.
        */}
      <div className="flex min-h-screen flex-col bg-slate-50 p-4">
        <header className="flex w-full shrink-0 justify-center py-2">
          <BrandLogo />
        </header>
        <main className="flex flex-1 flex-col items-center justify-start gap-4">
          {renderStep()}
        </main>
      </div>
    </I18nProvider>
  )

  function renderStep() {
    /**
     * The watchdog may take a LOADING screen away from a patient. It may not take a scan away.
     *
     * Every other stage it covers sits behind a `LoadingScreen`, which costs nothing to replace. The
     * scanning screen owns the `<video>` and the `<canvas>` the vendor is already reading frames off
     * and drawing into, so putting anything in front of it unmounts them - which kills a download
     * that was only slow, and, if that download lands anyway, hands the patient back a scanning
     * screen whose preview is a new element the SDK was never given. The prologue's budget buys
     * reloads; when it is spent, the vendor's own budget is what ends the wait and its retry screen
     * is what the patient taps (docs/decisions.md D-46).
     */
    if (exhausted && state.step !== 'scanning') {
      return <CouldNotLoadScreen onRetry={() => thisPage.reload()} />
    }

    switch (state.step) {
      case 'challenging':
        return <LoadingScreen />
      case 'date-of-birth':
        return (
          <DateOfBirthScreen
            token={token}
            onVerified={(verified) => {
              storeScanSession(token, verified)
              setState({ step: 'loading-config', accessToken: verified.accessToken })
            }}
            onDead={() => setState({ step: 'dead-link' })}
          />
        )
      case 'dead-link':
        return <DeadLinkScreen />
      case 'rate-limited':
        return <RateLimitedScreen />
      case 'loading-config':
        return <LoadingScreen />
      case 'scan-unavailable':
        return <ScanUnavailableScreen />
      case 'ready':
        return <StartScreen onStart={onStart} onLanguageChange={chooseLanguage} />
      case 'opening-camera':
        return <CameraPermissionScreen />
      case 'scanning':
        return (
          <ScanningScreen
            troubles={troubles}
            progress={progress}
            phase={phase}
            vendor={state.config.vendor}
            previewAspect={previewAspect}
          />
        )
      case 'complete':
        return <CompleteScreen results={state.results} />
      case 'thanked':
        return <ThankedScreen />
      case 'scan-failed':
        return (
          <ScanFailedScreen
            messageKey={state.messageKey}
            onRetry={() =>
              setState({ step: 'ready', accessToken: state.accessToken, config: state.config })
            }
          />
        )
      default:
        return null
    }
  }
}

/**
 * What this app calls a failure, from the sentence it is about to show the patient.
 *
 * ONE PLACE, and derived rather than passed in at each call site, because the two vocabularies serve
 * different readers and must not be confused for one another: the key names what a PATIENT reads, in
 * their own language, and the code names what a person triaging a ticket reads. A call site that
 * chose both would eventually have one drift from the other.
 *
 * Anything unmapped is `unexpected-error` rather than a guess. A code is only worth anything to the
 * person reading it if it means what it says.
 */
function reasonFor(messageKey: TranslationKey): ScanFailureReason {
  switch (messageKey) {
    case 'noClearReading':
      return 'no-usable-result'
    case 'cameraUnavailable':
      return 'camera-unavailable'
    case 'scannerCouldNotLoad':
      return 'scanner-unavailable'
    case 'scanCouldNotStart':
      return 'scan-could-not-start'
    case 'couldNotSendResults':
      return 'submit-failed'
    default:
      return 'unexpected-error'
  }
}

function watchedStage(state: FlowState, phase: MindsetPhase | null): PreparationStage | null {
  if (state.step === 'challenging' || state.step === 'loading-config') return state.step
  if (state.step === 'scanning') return prologueStage(state.config.vendor, phase)
  return null
}

/**
 * The vendor prologue, watched only where the vendor's SILENCE IS EVIDENCE OF A STALL - which is a
 * question about the SDK and not about the flow (docs/decisions.md D-46).
 *
 * Everything excluded here is excluded because a reload would be a guess, and the wrong guess costs a
 * patient a scan that was working:
 *
 * - A MEASUREMENT, on any vendor. The patient has been holding still in front of a camera and cannot
 *   be asked for that minute twice.
 * - FACEHEART, all of it. That SDK reports no phase and no progress at any point of a scan
 *   (fh-vitals-session.ts), so this page cannot tell its prologue from its measurement, and the one
 *   outcome strictly worse than the bug is reloading a scan that was measuring.
 * - MINDSET'S DOWNLOAD. Nothing it emits between `init()` and the licence call reaches this page:
 *   `ready` is consumed inside `observeMindsetClient` to resolve a promise, and `timeLeft` is a
 *   measurement signal. So 55 MB arriving perfectly normally is indistinguishable from 55 MB not
 *   arriving at all, and a window over it could only be a deadline from the tap. That is D-34's
 *   mistake, which cost patients working scans once already.
 *
 * What is left is the prologue of a vendor that talks: Shen.AI's download reports the fraction that
 * has landed as it lands (D-34), and either vendor's licence call, which is one request to one host -
 * Shen.AI's already bounded at twenty seconds, Mindset's `authorize()` bounded by nothing at all.
 */
function prologueStage(vendor: ScanVendor, phase: MindsetPhase | null): PreparationStage | null {
  if (phase === 'measuring') return null
  if (vendor === 'FaceHeart') return null
  if (vendor === 'Mindset' && (phase === null || phase === 'loading')) return null
  return 'vendor-prologue'
}
